import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Modal } from 'react-native';
import '../components/CommonButton.css'; // Importo i nuovi stili comuni
import { useCompetition } from '../contexts/CompetitionContext';
import {
  UsersIcon,
  UserIcon,
  PirateIcon,
  FencingSwordsIcon,
  BookIcon
} from '../components/FencingIcons';

// Definizione stili
const styles = StyleSheet.create({
  // Stili per il modal di conferma
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 20,
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 15,
    color: '#333',
  },
  modalText: {
    fontSize: 16,
    marginBottom: 20,
    lineHeight: 22,
    color: '#555',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 10,
  },
  modalButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 5,
    marginLeft: 10,
    minWidth: 100,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#f0f0f0',
  },
  confirmButton: {
    backgroundColor: '#d32f2f',
  },
  modalButtonText: {
    fontWeight: '600',
  },
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: '#f5f5f5',
  },
  header: {
    alignItems: 'center',
    marginTop: 24,
    marginBottom: 32,
    width: '100%',
  },
  title: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  blueDivider: {
    height: 6,
    width: 80,
    backgroundColor: '#1976d2',
    borderRadius: 3,
    marginTop: 8,
  },
  section: {
    marginBottom: 32,
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 16,
    color: '#424242',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    paddingBottom: 8,
  },
  cardGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    alignItems: 'stretch',
    marginHorizontal: -4,
  },
  card: {
    width: '30%',
    minWidth: 90,
    maxWidth: 120,
    margin: 6,
    padding: 16,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 110,
    boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.05)',
  },
  cardPressed: {
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
    transform: [{ scale: 0.97 }],
  },
  cardIcon: {
    fontSize: 42,
    marginBottom: 10,
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
    color: '#333333',
  },
  footer: {
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 40,
  },
  guideButton: {
    backgroundColor: '#f5f5f5',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 30,
    borderWidth: 1,
    borderColor: '#1976d2',
  },
  guideButtonText: {
    color: '#1976d2',
    fontWeight: 'bold',
    fontSize: 16,
  },
  navTip: {
    fontSize: 14,
    color: '#757575',
    fontStyle: 'italic',
    marginTop: 16,
    textAlign: 'center',
  },
  copyright: {
    color: '#9e9e9e',
    fontSize: 14,
    textAlign: 'center',
  },
});

const HomePage = () => {
  const navigate = useNavigate();
  const { teams, matches, resetAll, setCompetitionType } = useCompetition();

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [pendingCompetitionType, setPendingCompetitionType] = useState(null);
  const [showCompetitionConfirm, setShowCompetitionConfirm] = useState(false);

  // Verifica se esiste già una competizione
  const hasExistingCompetition = teams.length > 0 || matches.length > 0;


  const confirmReset = () => {
    // Usa il context per resettare tutto
    resetAll();
    // Pulisci anche altre chiavi non gestite dal context
    localStorage.clear();
    // Ricarica la pagina per applicare le modifiche
    window.location.reload();
  };

  // Gestisce la selezione del tipo di competizione
  const handleCompetitionTypeSelection = (type) => {
    // Se c'è già una competizione in corso, chiedi conferma
    if (hasExistingCompetition) {
      setPendingCompetitionType(type);
      setShowCompetitionConfirm(true);
    } else {
      proceedToCompetition(type);
    }
  };

  // Procedi con la creazione della competizione
  const proceedToCompetition = (type) => {
    // Pulisci i dati esistenti usando il context
    resetAll();

    // Pulisci anche altre chiavi legacy non gestite dal context
    const keysToRemove = [
      'individual_numPools', 'individual_schema', 'individual_manual_pools',
      'individual_sbarramento', 'individual_stoccateGirone', 'individual_stoccateElimDiretta',
      'individual_teams', 'individual_matches', 'individual_bracket',
      'teams_data', 'team_config', 'team_matches', 'team_bracket',
      'team_round_robin', 'team_elimination', 'team_final_ranking',
      'mixed_config', 'mixed_teams', 'mixed_matches', 'mixed_bracket',
      'mixed_results', 'mixed_ranking',
      'match_results', 'current_round', 'round_robin_results', 'elimination_results',
      'final_ranking', 'pool_results', 'direct_elimination', 'ranking', 'settings',
      'last_competition_type', 'tournament_state', 'current_stage'
    ];
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Imposta il tipo di competizione usando il context
    setCompetitionType(type);

    // Reindirizza alla pagina di configurazione appropriata
    switch (type) {
      case 'individual':
        navigate('/config-individuale');
        break;
      case 'team':
        navigate('/config-squadre');
        break;
      case 'mixed':
        navigate('/config-miste');
        break;
      default:
        navigate('/');
    }
  };

  // Card della dashboard
  const DashboardCard = ({ title, icon: Icon, color, onClick, disabled, style }) => {
    const [isPressed, setIsPressed] = useState(false);

    return (
      <TouchableOpacity
        onPressIn={() => setIsPressed(true)}
        onPressOut={() => setIsPressed(false)}
        onPress={onClick}
        disabled={disabled}
        style={[styles.card, { backgroundColor: color }, style, isPressed && styles.cardPressed]}
      >
        <Icon style={styles.cardIcon} />
        <Text style={styles.cardTitle}>{title}</Text>
      </TouchableOpacity>
    );
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title} className="page-title">123 Scherma!</Text>
        <View style={styles.blueDivider} />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Crea Nuova Competizione</Text>
        <View style={styles.cardGrid}>
          <DashboardCard
            title="Gara a Squadre"
            icon={UsersIcon}
            color="#e3f2fd"
            onClick={() => handleCompetitionTypeSelection('team')}
            style={{ borderLeftWidth: 4, borderLeftColor: '#1976d2' }}
          />
          <DashboardCard
            title="Gara Individuale"
            icon={UserIcon}
            color="#e8f5e9"
            onClick={() => handleCompetitionTypeSelection('individual')}
            style={{ borderLeftWidth: 4, borderLeftColor: '#43a047' }}
          />
          <DashboardCard
            title="Squadre Miste"
            icon={PirateIcon}
            color="#fff3e0"
            onClick={() => handleCompetitionTypeSelection('mixed')}
            style={{ borderLeftWidth: 4, borderLeftColor: '#ff9800' }}
          />
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Altro</Text>
        <View style={styles.cardGrid}>
          <DashboardCard
            title="Segnapunti"
            icon={FencingSwordsIcon}
            color="#e8eaf6"
            onClick={() => navigate('/tools/segnapunti')}
            style={{ borderLeftWidth: 4, borderLeftColor: '#3f51b5' }}
          />
          <DashboardCard
            title="Guida all'uso"
            icon={BookIcon}
            color="#e1f5fe"
            onClick={() => navigate('/guida')}
            style={{ borderLeftWidth: 4, borderLeftColor: '#03a9f4' }}
          />
        </View>
      </View>

      {/* Dialog di conferma reset */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={showResetConfirm}
        onRequestClose={() => setShowResetConfirm(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Conferma Reset</Text>
            <Text style={styles.modalText}>
              Sei sicuro di voler cancellare TUTTI i dati salvati? Questa azione non può essere annullata.
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setShowResetConfirm(false)}
              >
                <Text style={styles.modalButtonText}>Annulla</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.confirmButton]}
                onPress={confirmReset}
              >
                <Text style={[styles.modalButtonText, { color: 'white' }]}>Elimina Tutto</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Dialog di conferma nuova competizione */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={showCompetitionConfirm}
        onRequestClose={() => setShowCompetitionConfirm(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Attenzione</Text>
            <Text style={styles.modalText}>
              Esiste già una competizione in corso. Procedendo, tutti i dati della competizione corrente verranno cancellati.
              Sei sicuro di voler continuare?
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setShowCompetitionConfirm(false)}
              >
                <Text style={styles.modalButtonText}>Annulla</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.confirmButton]}
                onPress={() => {
                  proceedToCompetition(pendingCompetitionType);
                  setShowCompetitionConfirm(false);
                }}
              >
                <Text style={[styles.modalButtonText, { color: 'white' }]}>Conferma</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <View style={styles.footer}>
        {/* Spazio per eventuali elementi futuri */}
      </View>
    </ScrollView>
  );
};

export default HomePage;
